// packages/frontend/src/services/idConsistencyService.ts

import { useEnhancedAuthStore } from '../stores/enhancedAuth';
import { createLogger } from '../utils/logger';

const logger = createLogger('IdConsistencyService');

/**
 * Service to ensure consistent ID usage between upload and retrieval operations
 * This prevents the creation of multiple folders for the same therapist/client combination
 */
export class IdConsistencyService {
  private static instance: IdConsistencyService;
  private cachedTherapistId: string | null = null;
  private cachedClientId: string | null = null;

  private constructor() {}

  public static getInstance(): IdConsistencyService {
    if (!IdConsistencyService.instance) {
      IdConsistencyService.instance = new IdConsistencyService();
    }
    return IdConsistencyService.instance;
  }

  /**
   * Get the consistent therapist ID for both upload and retrieval operations
   * Uses Firebase Auth UID (not internal UUID) to match backend expectations
   * This ensures the same ID is used across all operations
   */
  public getTherapistId(): string {
    const { firebaseUser, isLoading, isInitialized, authProvider } = useEnhancedAuthStore.getState();
    
    // Debug-level: this runs on every id lookup; INFO here was log spam.
    logger.debug('IdConsistencyService: getTherapistId called', {
      component: 'IdConsistencyService',
      metadata: {
        hasFirebaseUser: !!firebaseUser,
        firebaseUserId: firebaseUser?.uid || null,
        isLoading,
        isInitialized,
        authProvider,
        timestamp: new Date().toISOString()
      }
    });

    if (!firebaseUser?.uid) {
      logger.error('IdConsistencyService: No firebaseUser found', {
        component: 'IdConsistencyService',
        metadata: {
          isLoading,
          isInitialized,
          authProvider,
          timestamp: new Date().toISOString()
        }
      });
      throw new Error('No authenticated therapist found');
    }

    // Cache the therapist ID to ensure consistency
    // Use Firebase Auth UID instead of internal UUID
    if (!this.cachedTherapistId) {
      this.cachedTherapistId = firebaseUser.uid;
      logger.info('Cached therapist ID for consistency', {
        component: 'IdConsistencyService',
        metadata: { therapistId: this.cachedTherapistId }
      });
    }

    return this.cachedTherapistId;
  }

  /**
   * Get the consistent client ID for both upload and retrieval operations
   * This ensures the same ID is used across all operations
   */
  public getClientId(clientId: string): string {
    if (!clientId) {
      throw new Error('Client ID is required');
    }

    // Always set current client ID; do NOT reuse a previous client's ID across operations
    this.cachedClientId = clientId;
    return clientId;
  }

  /**
   * Get both therapist and client IDs consistently
   * This is the main method to use for all upload/retrieval operations
   */
  public getConsistentIds(clientId: string): { therapistId: string; clientId: string } {
    const therapistId = this.getTherapistId();
    const consistentClientId = this.getClientId(clientId);

    logger.info('Retrieved consistent IDs for operations', {
      component: 'IdConsistencyService',
      metadata: { 
        therapistId, 
        clientId: consistentClientId,
        cachedTherapistId: this.cachedTherapistId,
        cachedClientId: this.cachedClientId
      }
    });

    return {
      therapistId,
      clientId: consistentClientId
    };
  }

  /**
   * Clear cached IDs (useful when switching users or clients)
   */
  public clearCache(): void {
    logger.info('Clearing ID cache', {
      component: 'IdConsistencyService',
      metadata: { 
        previousTherapistId: this.cachedTherapistId,
        previousClientId: this.cachedClientId
      }
    });

    this.cachedTherapistId = null;
    this.cachedClientId = null;
  }

  /**
   * Get cache status for debugging
   */
  public getCacheStatus(): { therapistId: string | null; clientId: string | null } {
    return {
      therapistId: this.cachedTherapistId,
      clientId: this.cachedClientId
    };
  }
}

// Export singleton instance
export const idConsistencyService = IdConsistencyService.getInstance();
